import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

/**
 * Renders a dynamic form based on collection schema fields
 * @param {Array} fields - Array of field definitions from schema
 * @param {object} formData - Current form data state
 * @param {function} setFormData - Function to update form data
 * @param {object} references - Reference data for select fields (e.g., { projects: [...] })
 */
const DynamicForm = ({ fields, formData, setFormData, references = {} }) => {
    
    const handleChange = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const renderField = (field) => {
        const value = formData[field.name] ?? field.default ?? "";
        
        switch (field.type) {
            case "select":
                return (
                    <Select
                        value={value || "none"}
                        onValueChange={(v) => handleChange(field.name, v === "none" ? "" : v)}
                    >
                        <SelectTrigger className="input-industrial" data-testid={`field-${field.name}`}>
                            <SelectValue placeholder={`Selecciona ${field.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                            {!field.required && (
                                <SelectItem value="none">Sin especificar</SelectItem>
                            )}
                            {field.options?.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );

            case "reference":
                const refData = references[field.ref_collection] || [];
                return (
                    <Select
                        value={value || "none"}
                        onValueChange={(v) => handleChange(field.name, v === "none" ? "" : v)}
                    >
                        <SelectTrigger className="input-industrial" data-testid={`field-${field.name}`}>
                            <SelectValue placeholder={`Selecciona ${field.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Sin asignar</SelectItem>
                            {refData.map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                    {item.nombre || item.name || item.title || item.plate || item.id}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );

            case "multi_reference": {
                const options = references[field.ref_collection] || [];
                const selected = Array.isArray(value) ? value : [];
                const toggle = (id) => {
                    const next = selected.includes(id)
                        ? selected.filter((x) => x !== id)
                        : [...selected, id];
                    handleChange(field.name, next);
                };
                return (
                    <div
                        className="border border-[#E5E5E5] rounded-sm max-h-52 overflow-y-auto divide-y divide-[#F0F0F0]"
                        data-testid={`field-${field.name}`}
                    >
                        {options.length === 0 ? (
                            <p className="text-sm text-[#999] p-3">No hay usuarios disponibles</p>
                        ) : (
                            options.map((item) => {
                                const label = item.nombre || item.name || item.title || item.id;
                                const on = selected.includes(item.id);
                                return (
                                    <label
                                        key={item.id}
                                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8F9FA]"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={on}
                                            onChange={() => toggle(item.id)}
                                            className="w-4 h-4 accent-[#0055FF]"
                                        />
                                        <span className="text-sm">{label}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                );
            }

            case "text":
                return (
                    <Textarea
                        value={value}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        className="input-industrial min-h-[80px]"
                        placeholder={field.label}
                        data-testid={`field-${field.name}`}
                    />
                );

            case "number":
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => handleChange(field.name, parseFloat(e.target.value) || 0)}
                        className="input-industrial"
                        placeholder={field.label}
                        data-testid={`field-${field.name}`}
                    />
                );

            case "date":
                return (
                    <Input
                        type="date"
                        value={value}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        className="input-industrial"
                        data-testid={`field-${field.name}`}
                    />
                );

            case "boolean":
                return (
                    <Select
                        value={String(value || false)}
                        onValueChange={(v) => handleChange(field.name, v === "true")}
                    >
                        <SelectTrigger className="input-industrial" data-testid={`field-${field.name}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="true">Sí</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                    </Select>
                );

            case "array":
                // Arrays are handled specially (e.g., photos)
                return null;

            case "string":
            default:
                return (
                    <Input
                        value={value}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        className="input-industrial"
                        placeholder={field.label}
                        required={field.required}
                        data-testid={`field-${field.name}`}
                    />
                );
        }
    };

    // Filter out array fields (handled separately) and organize layout
    const visibleFields = fields.filter(f => f.type !== "array");

    // Group fields for 2-column layout (small fields) vs full-width (text / multi_reference)
    const wideTypes = ["text", "multi_reference"];
    const smallFields = visibleFields.filter(f => !wideTypes.includes(f.type));
    const textFields = visibleFields.filter(f => wideTypes.includes(f.type));

    return (
        <div className="space-y-4">
            {/* Small fields in 2-column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {smallFields.map((field) => (
                    <div key={field.name}>
                        <Label className="label-overline mb-2 block">
                            {field.label || field.name}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {renderField(field)}
                    </div>
                ))}
            </div>
            
            {/* Text fields full width */}
            {textFields.map((field) => (
                <div key={field.name}>
                    <Label className="label-overline mb-2 block">
                        {field.label || field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderField(field)}
                </div>
            ))}
        </div>
    );
};

export default DynamicForm;
